import { useEffect, useRef, useState } from "react";
import { api } from "../api";

/**
 * Sign in with email: send a 6-digit code (and a link) to the address, then type the code.
 * Opening the link lands on /login?email_token=…, which Login passes here as `linkToken`.
 */
export function EmailSignIn({ target, linkToken }: { target: string; linkToken: string | null }) {
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem("ccnote.loginEmail") ?? "";
    } catch {
      return "";
    }
  });
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const redirect = `${location.origin}${target}`;

  const go = (r: { redirect: string }) => window.location.assign(r.redirect);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // The link from the email: confirm it (a POST, so mail scanners that open links can't use it up).
  useEffect(() => {
    if (!linkToken) return;
    void run(async () => go(await api<{ redirect: string }>("/auth/email/link", { method: "POST", body: JSON.stringify({ token: linkToken }) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken]);

  const send = () =>
    run(async () => {
      await api("/auth/email/start", { method: "POST", body: JSON.stringify({ email, redirect }) });
      try {
        localStorage.setItem("ccnote.loginEmail", email.trim());
      } catch {
        // private mode: fine
      }
      setStep("code");
      setCode("");
      setTimeout(() => codeRef.current?.focus(), 0);
    });
  const verify = (value = code) =>
    run(async () => go(await api<{ redirect: string }>("/auth/email/verify", { method: "POST", body: JSON.stringify({ email, code: value }) })));

  if (linkToken && (busy || !error)) return <p className="muted center">Signing you in…</p>;

  return (
    <div className="email-signin">
      {step === "email" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) void send();
          }}
        >
          <label className="sr-only" htmlFor="login-email">
            Email
          </label>
          <input id="login-email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button className="btn" disabled={busy || !email.trim()}>
            {busy ? "Sending…" : "Email me a sign-in code"}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p className="muted small">
            We sent a 6-digit code to <strong>{email.trim()}</strong>. Type it here, or open the link in the email.
          </p>
          <label className="sr-only" htmlFor="login-code">
            Code
          </label>
          <input
            id="login-code"
            ref={codeRef}
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123 456"
            maxLength={7}
            value={code}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d ]/g, "");
              setCode(value);
              if (value.replace(/\D/g, "").length === 6) void verify(value);
            }}
          />
          <button className="btn btn-primary" disabled={busy || code.replace(/\D/g, "").length !== 6}>
            {busy ? "Checking…" : "Sign in"}
          </button>
          <p className="muted small">
            <button type="button" className="link-btn" disabled={busy} onClick={() => void send()}>
              Send a new code
            </button>{" "}
            ·{" "}
            <button type="button" className="link-btn" onClick={() => setStep("email")}>
              Use another email
            </button>
          </p>
        </form>
      )}
      {error && <p className="alert">{error}</p>}
    </div>
  );
}
